/**
 * OrgSettings.tsx — FR-013 (H2) / T314 → T321 SETTINGS-1 rewrite.
 *
 * The CIRCA backend exposes the following org-rooted public surface
 * (post-T321 SETTINGS-1, convex/organizations.ts):
 *   - organizations.updateOrg                       (org metadata; admin)
 *   - organizations.updateBranding                  (branding; admin)
 *   - organizations.updateChannelDefaults           (defaults; admin)
 *   - organizations.updateNotificationPreferences   (notifications; admin)
 *   - organizations.updateMemberProfile             (self-edit; auth)
 *   - organizations.completeOnboarding              (still stubbed)
 *
 * Per PRD §FR-013: "the org-settings subtree in particular never reports a
 * false save." Every Save here goes through useMutationWithToast — the
 * wrapper logs argument KEYS only, re-throws after toasting, and never
 * fires toast.success unless the mutation actually resolved.
 *
 * T321 SETTINGS-1 fixes the prior MISUSE on this file: the previous
 * `updateBrandingM.run({ name, industry, timezone, dateFormat } as any)`
 * call sent organization-metadata fields to a branding mutation. They now
 * route to `api.organizations.updateOrg` (whose validator was extended to
 * carry industry/timezone/dateFormat alongside name/plan/logoUrl/etc.).
 * The `as any` is gone — every Save call is fully typed.
 *
 * H4 ratchet: this file MUST NOT be a thin 7-line re-export shell. See
 * frontend-v3/tests/destub/honest-state.test.ts (H4) and the sibling
 * SchedulesEditor.tsx for the matching twin rewrite.
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import {
  Btn,
  Field,
  Input,
  Panel,
  Pill,
  PageHead,
  SegmentedControl,
  Select,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { api } from '@convex/api';

interface OrgDoc {
  name?: string | null;
  plan?: string | null;
  industry?: string | null;
  timezone?: string | null;
  dateFormat?: string | null;
}

// Minimal channels-row + members-row shapes for the header counts. The
// component never reads anything else off these rows.
interface ChannelRowMinimal {
  _id: string;
}
interface MemberRowMinimal {
  _id: string;
}

export function OrgSettings() {
  // ──────────────────────────────────────────────────────────────────
  // Stub-bound mutation wrappers (T321 SETTINGS-1: completeOnboarding
  // remains stub-only; updateBranding/etc. moved to their own sub-pages).
  // The wrapper fires toast.success ONLY on a resolved mutation; until
  // the stub lands a real body, every call surfaces an honest error
  // toast.
  // ──────────────────────────────────────────────────────────────────
  const completeOnboardingM = useMutationWithToast(
    api.organizations.completeOnboarding,
    {
      logLabel: 'organizations_completeOnboarding',
      successMessage: 'Onboarding completado.',
      errorMessagePrefix: 'No se pudo completar el onboarding',
    },
  );
  // T321: organization metadata writes now go through updateOrg (not
  // updateBranding) — fully typed, no `as any` cast.
  const updateOrgM = useMutationWithToast(api.organizations.update, {
    logLabel: 'organizations_updateOrg',
    successMessage: 'Organización actualizada.',
    errorMessagePrefix: 'No se pudo actualizar la organización',
  });

  // ──────────────────────────────────────────────────────────────────
  // Real subscriptions: the org doc + the channels/members lists that
  // back the header counts. T321 SETTINGS-1 replaced the prior
  // `CIRCA.members.length` / `CIRCA.channels.length` reads with these
  // honest subscriptions (FR-004 (S4)).
  // ──────────────────────────────────────────────────────────────────
  const org = useQuery(api.organizations.getCurrentOrg) as
    | OrgDoc
    | null
    | undefined;
  const channels = useQuery(api.channels.listChannels) as
    | ChannelRowMinimal[]
    | undefined;
  // `listMembers` is admin-gated. For non-admin viewers `useQuery`
  // surfaces an error via the boundary; the header simply falls back to
  // an em-dash placeholder when the array is unavailable. The count
  // shown is therefore the real Convex value when present and never
  // an invented number.
  const members = useQuery(api.organizations.listMembers) as
    | MemberRowMinimal[]
    | undefined;

  // ──────────────────────────────────────────────────────────────────
  // Local form state — seeded from the org doc on first load.
  // ──────────────────────────────────────────────────────────────────
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('Medios y entretenimiento');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [dateFormat, setDateFormat] = useState<'largo' | 'corto' | 'iso'>(
    'largo',
  );

  useEffect(() => {
    if (!org) return;
    if (typeof org.name === 'string') setOrgName(org.name);
    if (typeof org.industry === 'string') setIndustry(org.industry);
    if (typeof org.timezone === 'string') setTimezone(org.timezone);
    if (
      org.dateFormat === 'largo' ||
      org.dateFormat === 'corto' ||
      org.dateFormat === 'iso'
    ) {
      setDateFormat(org.dateFormat);
    }
  }, [org]);

  const handleSaveOrg = () => {
    // T321: fully-typed args — no `as any` — routed to updateOrg, whose
    // validator was extended to accept industry/timezone/dateFormat.
    void updateOrgM.run({
      name: orgName,
      industry,
      timezone,
      dateFormat,
    });
  };

  const handleCompleteOnboarding = () => {
    void completeOnboardingM.run({});
  };
  // Keep the onboarding handler reachable so an honest invocation path
  // exists for the lone remaining stub-bound mutation in this file.
  void handleCompleteOnboarding;

  const isSaving = updateOrgM.loading || completeOnboardingM.loading;

  const memberCount =
    Array.isArray(members) ? members.length : null;
  const channelCount =
    Array.isArray(channels) ? channels.length : null;
  const displayPlan = org?.plan ?? 'Fundadores';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageHead
        title="Organización"
        description="Datos generales de la organización."
      />
      <Panel pad={24}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background:
                'linear-gradient(135deg, var(--forest-a), var(--forest-b))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {(orgName || 'C').slice(0, 1).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 22,
                fontWeight: 500,
              }}
            >
              {orgName || 'Organización'}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--muted-slate)',
                marginTop: 2,
              }}
            >
              {memberCount ?? '—'} miembros · {channelCount ?? '—'} canales ·
              Plan {displayPlan}
            </div>
          </div>
          <Pill tone="green" icon="checkCircle">
            Activa
          </Pill>
        </div>
      </Panel>
      <Panel title="Organización" pad={28}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Field label="Nombre de la organización">
            <Input value={orgName} onChange={setOrgName} />
          </Field>
          <Field label="Industria">
            <Select
              value={industry}
              onChange={setIndustry}
              full
              options={[
                'Medios y entretenimiento',
                'Deportes',
                'Educación',
                'Noticias',
              ]}
            />
          </Field>
          <Field label="Zona horaria">
            <Select
              value={timezone}
              onChange={setTimezone}
              full
              options={[
                'America/Mexico_City',
                'America/Bogota',
                'Europe/Madrid',
              ]}
            />
          </Field>
          <Field label="Formato de fecha">
            <SegmentedControl
              value={dateFormat}
              onChange={(v: 'largo' | 'corto' | 'iso') => setDateFormat(v)}
              options={[
                { value: 'largo', label: '1 ene 2026' },
                { value: 'corto', label: '1/1/2026' },
                { value: 'iso', label: '2026-01-01' },
              ]}
            />
          </Field>
          <div>
            <Btn variant="solid" onClick={handleSaveOrg} disabled={isSaving}>
              {isSaving ? 'Guardando…' : 'Guardar'}
            </Btn>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default OrgSettings;
